// React & External Libraries
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import type { FeatureCollection } from 'geojson';
import JoyRide from 'react-joyride';
import type { Step, TooltipRenderProps } from 'react-joyride';

// Store
import { useMapStore } from './MapStore/UseMapStore';

// App Hooks & Services
import { Api } from '@services';
import { isAdmin, isReadOnly, isSuperAdmin, isSystemAdmin, isUser } from '@utils';

// Components
import { Loading } from '@components';
import { SonarMapTourModal } from '../../../../components/layout/SonarMapTour';
import { MapObject } from '../..';

// Tour Steps
import {
  AdminSteps,
  ReadOnlySteps,
  UserSteps,
} from '../../../../components/layout/SonarMapTour/SonarMapTourSteps';

// Styles
import '../../../../components/layout/SonarMapTour/SonarMapTourModal.scss';

// Define the current map tour version (increment this when a new version is released)
const MAP_TOUR_VERSION = 'v1'; // Current version of the map tour

export function MapContainer() {
  const location = useLocation();

  // Map Info Slice
  const mapID = useMapStore((state) => state.mapID);
  const setMapID = useMapStore((state) => state.setMapID);
  const URLArr = useMapStore((state) => state.URLArr);
  const setURLArr = useMapStore((state) => state.setURLArr);
  const setMapName = useMapStore((state) => state.setMapName);
  const setMapDescription = useMapStore((state) => state.setMapDescription);
  const setMapKeywords = useMapStore((state) => state.setMapKeywords);
  const setMapKey = useMapStore((state) => state.setMapKey);
  const setIsStatic = useMapStore((state) => state.setIsStatic);

  // Map Data Slice (Feature Collections, Map Image, Lists)
  const mapImg = useMapStore((state) => state.mapImg);
  const setMapImg = useMapStore((state) => state.setMapImg);
  const overlayFC = useMapStore((state) => state.overlayFC);
  const setOverlayFC = useMapStore((state) => state.setOverlayFC);
  const fetchMapImage = useMapStore((state) => state.fetchMapImage);
  const fetchMapOveraly = useMapStore((state) => state.fetchMapOveraly);

  // Map UI Slice (Tour, Loading, Modal)
  const isTourStarted = useMapStore((state) => state.isTourStarted);
  const setIsTourStarted = useMapStore((state) => state.setIsTourStarted);
  const showMapTourModal = useMapStore((state) => state.showMapTourModal);
  const setShowMapTourModal = useMapStore((state) => state.setShowMapTourModal);
  const isLoading = useMapStore((state) => state.isLoading);
  const setIsLoading = useMapStore((state) => state.setIsLoading);

  // Data Refresh
  const setImageRefetch = useMapStore((state) => state.setImageRefetch);
  const setOverlayRefetch = useMapStore((state) => state.setOverlayRefetch);

  const stepsToUse = (
    isUser()
      ? UserSteps
      : isReadOnly()
      ? ReadOnlySteps
      : isSystemAdmin() || isAdmin() || isSuperAdmin()
      ? AdminSteps
      : []
  ) as Step[];

  // Map data queries
  const initImg = useQuery([`map-${mapID}-image`], async () => await fetchMapImage(), {
    enabled: !!mapID,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setMapImg(data);
    },
  });

  const mapOverlayRes = useQuery([`map-${mapID}-overlay`], () => fetchMapOveraly(), {
    enabled: !!mapID,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setOverlayFC(data);
      setIsStatic((data as FeatureCollection).features[0].properties.MapType === 'Static');
    },
  });

  // Reset on map store open
  useEffect(() => {
    useMapStore.getState().resetAllSlices();
  }, []);

  // Store refetch in Zustand for external use
  useEffect(() => {
    if (initImg.refetch) {
      setImageRefetch(() => initImg.refetch);
    }
  }, [initImg.refetch, setImageRefetch]);

  // Store refetch in Zustand for external use
  useEffect(() => {
    if (mapOverlayRes.refetch) {
      setOverlayRefetch(() => mapOverlayRes.refetch);
    }
  }, [mapOverlayRes.refetch, setOverlayRefetch]);

  useEffect(() => {
    setURLArr(location.pathname.split('/'));
  }, [location]);

  useEffect(() => {
    setMapID(
      URLArr[URLArr.length - 1] === 'edit' ? URLArr[URLArr.length - 2] : URLArr[URLArr.length - 1],
    );
  }, [URLArr]);

  useEffect(() => {
    if (initImg.isFetching || mapOverlayRes.isFetching || !overlayFC || !mapImg) {
      setIsLoading(true);
      return;
    }
    setIsLoading(false);

    setMapName(overlayFC.features[0].properties.Name ?? '');
    setMapKeywords(overlayFC.features[0].properties.Keywords ?? []);

    const currentDesc = useMapStore.getState().mapDescription;
    if (!currentDesc) {
      setMapDescription(overlayFC.features[0].properties.Description ?? '');
    }
  }, [
    initImg.isFetching,
    mapOverlayRes.isFetching,
    overlayFC,
    mapImg,
    setIsLoading,
    setMapName,
    setMapDescription,
    setMapKeywords,
  ]);

  // Check local storage to see if the user has already seen the current map tour
  useEffect(() => {
    // get Azure map key for displaying tiles
    Api.get(`/me/map-server-key`).then((resp) => {
      setMapKey(resp.data as any);
    });

    const seenTourVersion = localStorage.getItem('mapTourVersion');

    // If the user hasn't seen this version of the tour, show the modal
    if (seenTourVersion !== MAP_TOUR_VERSION) {
      const showTourModalTimeout = setTimeout(() => {
        setShowMapTourModal(true);
      }, 1000); // Delay before showing the modal

      return () => clearTimeout(showTourModalTimeout); // Clear timeout on component unmount
    }
  }, []);

  // Start the tour and mark the tour as seen
  const startMapTour = () => {
    setShowMapTourModal(false);
    setIsTourStarted(true);

    // Store the current version in localStorage to mark the tour as seen
    localStorage.setItem('mapTourVersion', MAP_TOUR_VERSION);
  };

  // Skip the tour and mark the tour as skipped
  const skipMapTour = () => {
    setShowMapTourModal(false);

    // Store the current version in localStorage to mark the tour as skipped
    localStorage.setItem('mapTourVersion', MAP_TOUR_VERSION);
  };

  const customToolTip = ({
    continuous,
    index,
    isLastStep,
    size,
    step,
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    tooltipProps,
  }: TooltipRenderProps) => {
    const tooltipArrowColor =
      step.title === 'Tools Menu' || step.title === 'Map Layers' || step.title === 'Info Menu'
        ? 'white'
        : 'var(--primary-to-black)';
    return (
      <div className="custom-tool-tip-tour" {...tooltipProps}>
        <div className="c-tool-tip-blue">
          <span className="c-tool-tip-title">{step.title && <h6>{step.title}</h6>}</span>

          <span
            className="c-tool-tip-exit"
            {...skipProps}
            onClick={() => {
              // setTourStarted(false);
              setIsTourStarted(false);
              localStorage.removeItem('newUserConfirmation');
            }}
          >
            <i className="sonarIcon sonarIcon-add fs-md"></i>
          </span>
        </div>

        <div className="c-tool-tip-content">
          {step.content && <div>{step.content}</div>}
          <div className="c-tool-tip-footer">
            <div>
              {index + 1}/{size}
            </div>
            {index > 0 && (
              <div className="c-tool-back" {...backProps}>
                <i className="sonarIcon sonarIcon-left-arrow fs-sm"></i>
              </div>
            )}
            {!isLastStep ? (
              <div className="c-tool-next" {...primaryProps}>
                <i className="sonarIcon sonarIcon-right-arrow fs-sm"></i>
              </div>
            ) : (
              <div className="c-tool-next" {...primaryProps}>
                <i className="sonarIcon sonarIcon-check fs-sm"></i>
              </div>
            )}
          </div>
        </div>
        {/* Setting arrow color dynamically */}
        <style>
          {`
          .__floater__arrow > span > svg > polygon {
            fill: ${tooltipArrowColor} !important;
          }
        `}
        </style>
      </div>
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      {/* Show the map tour modal */}
      <SonarMapTourModal
        show={showMapTourModal}
        onStartTour={startMapTour}
        onSkipTour={skipMapTour}
      />
      {/* JoyRide for Map Tour */}
      {isTourStarted && (
        <JoyRide
          steps={stepsToUse}
          continuous
          showProgress
          showSkipButton
          disableOverlayClose
          scrollToFirstStep
          tooltipComponent={customToolTip}
          locale={{
            last: 'Finish',
            skip: 'Skip',
          }}
          styles={{
            options: {
              backgroundColor: 'var(--primary)',
              primaryColor: 'var(--primary)',
              textColor: '#fff',
              zIndex: 1000,
            },
          }}
        />
      )}
      <MapObject />
    </>
  );
}
